/**
 * useUnifiedStockfishAnalysis.js - Unified state management for Stockfish analysis
 * Centralizes all analysis sources and cache checking logic to prevent excessive re-renders
 */

import { useReducer, useEffect, useRef, useCallback, useContext } from 'react';
import stockfish3Instance from '../util/Stockfish3';
import { ChessSocialContext } from '../util/ChessSocialContext';

// Initial state for the analysis reducer
const initialState = {
  analysis: null,
  isAnalyzing: false,
  isEngineReady: false,
  depth: 0,
  fen: null,
  source: null, // 'cache', 'mock', 'engine', 'database', 'window', 'external'
  lastUpdated: null,
  error: null
};

// Analysis state reducer
const analysisReducer = (state, action) => {
  switch (action.type) {
    case 'SET_ANALYZING':
      if (state.isAnalyzing === action.payload) return state; // No change
      return { ...state, isAnalyzing: action.payload, error: null };
    
    case 'SET_ENGINE_READY':
      if (state.isEngineReady === action.payload) return state; // No change
      return { ...state, isEngineReady: action.payload, error: null };
    
    case 'SET_ANALYSIS':
      // Deep equality check to prevent unnecessary updates
      if (isAnalysisEqual(state.analysis, action.payload.analysis)) {
        // console.log('ABC123 REDUCER: Preventing identical analysis update');
        return state; // No change, no re-render
      }
      
      // ENHANCED: Prevent downgrading from better analysis
      const currentDepth = state.analysis?.depth || 0;
      const currentPvCount = state.analysis?.pvs?.length || 0;
      const newDepth = action.payload.analysis?.depth || 0;
      const newPvCount = action.payload.analysis?.pvs?.length || 0;
      
      // Same FEN check
      const currentFEN = (state.analysis?.positionFEN || state.analysis?.fen || '').split(' ').slice(0, 4).join(' ');
      const newFEN = (action.payload.analysis?.positionFEN || action.payload.analysis?.fen || '').split(' ').slice(0, 4).join(' ');
      
      // If same FEN and new analysis is worse, keep current
      if (currentFEN === newFEN && currentFEN !== '') {
        if (currentDepth >= 15 && currentPvCount >= 3 && (newDepth < currentDepth || newPvCount < currentPvCount)) {
          // console.log('ABC123 REDUCER: Preventing downgrade from d=15 analysis:', {
          //   current: { depth: currentDepth, pvs: currentPvCount },
          //   new: { depth: newDepth, pvs: newPvCount }
          // });
          return state; // Keep better analysis
        }
      }
      
      // console.log('ABC123 REDUCER: Updating analysis:', {
      //   from: { depth: currentDepth, pvs: currentPvCount },
      //   to: { depth: newDepth, pvs: newPvCount }
      // });
      
      return {
        ...state,
        analysis: action.payload.analysis,
        depth: action.payload.analysis?.depth || state.depth,
        source: action.payload.source,
        isAnalyzing: false,
        lastUpdated: Date.now(),
        error: null
      };
    
    case 'SET_FEN':
      if (state.fen === action.payload) return state;
      return { ...state, fen: action.payload };
    
    case 'SET_ERROR':
      return { ...state, error: action.payload, isAnalyzing: false };
    
    case 'RESET':
      return initialState;
    
    default:
      return state;
  }
};

// Deep equality check for analysis data
const isAnalysisEqual = (a, b) => {
  if (!a || !b) return a === b;
  
  // Check key properties
  if (a.depth !== b.depth) return false;
  if (a.positionFEN !== b.positionFEN) return false;
  if ((a.pvs?.length || 0) !== (b.pvs?.length || 0)) return false;
  
  // Check first PV line for basic equality
  if (a.pvs?.[0]?.bestMoveLAN !== b.pvs?.[0]?.bestMoveLAN) return false;
  if (a.pvs?.[0]?.normalizedCP !== b.pvs?.[0]?.normalizedCP) return false;
  
  return true;
};

// Helper function to check if analysis data matches the current FEN
const analysisMatchesCurrentFEN = (data, currentFEN) => {
  if (!data || !currentFEN) return false;
  
  // Check if the analysis data is for this specific position
  const analysisFEN = data.fen || data.positionFEN;
  if (!analysisFEN) return false;
  
  // Compare the FENs, but ignore the move counters (last 2 fields)
  const currentFENBase = currentFEN.split(' ').slice(0, 4).join(' ');
  const analysisFENBase = analysisFEN.split(' ').slice(0, 4).join(' ');
  
  return currentFENBase === analysisFENBase;
};

// Get cache key from FEN (first 4 parts only, ignore move counters)
const getCacheKey = (fenStr) => {
  return fenStr?.split(' ').slice(0, 4).join(' ') || '';
};

// Mock data for starting position
const getStartingPositionAnalysis = () => {
  const startingFEN = 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';
  
  return {
    fen: startingFEN,
    depth: 15,
    lines: [
      {
        move: 'e4',
        score: '+0.30',
        pv: ['e2e4', 'e7e5', 'g1f3', 'b8c6', 'f1b5']
      },
      {
        move: 'd4',
        score: '+0.25',
        pv: ['d2d4', 'd7d5', 'c2c4', 'e7e6', 'b1c3']
      },
      {
        move: 'Nf3',
        score: '+0.20',
        pv: ['g1f3', 'd7d5', 'd2d4', 'g8f6', 'c2c4']
      }
    ],
    isComplete: true,
    isFromCache: false,
    pvs: [
      {
        line: 1,
        evaluation: '+0.30',
        normalizedCP: 0.30,
        bestMoveLAN: 'e2e4',
        bestMoveSAN: 'e4',
        pv: ['e2e4', 'e7e5', 'g1f3', 'b8c6', 'f1b5'],
        pvSAN: ['e4', 'e5', 'Nf3', 'Nc6', 'Bb5'],
        userOrientation: 'white',
        positionTurn: 'white',
        depth: 15
      },
      {
        line: 2,
        evaluation: '+0.25',
        normalizedCP: 0.25,
        bestMoveLAN: 'd2d4',
        bestMoveSAN: 'd4',
        pv: ['d2d4', 'd7d5', 'c2c4', 'e7e6', 'b1c3'],
        pvSAN: ['d4', 'd5', 'c4', 'e6', 'Nc3'],
        userOrientation: 'white',
        positionTurn: 'white',
        depth: 15
      },
      {
        line: 3,
        evaluation: '+0.20',
        normalizedCP: 0.20,
        bestMoveLAN: 'g1f3',
        bestMoveSAN: 'Nf3',
        pv: ['g1f3', 'd7d5', 'd2d4', 'g8f6', 'c2c4'],
        pvSAN: ['Nf3', 'd5', 'd4', 'Nf6', 'c4'],
        userOrientation: 'white',
        positionTurn: 'white',
        depth: 15
      }
    ],
    scores: ['+0.3', '+0.25', '+0.20'],
    positionFEN: startingFEN,
    timestamp: Date.now(),
    isAnalysisOnly: true
  };
};

// Main unified hook for Stockfish analysis
export const useUnifiedStockfishAnalysis = (fen, options = {}) => {
  const {
    externalData = null,
    checkWindowCache = true,
    checkDatabaseCache = true,
    forceLive = false,
    targetDepth = 15,
    onAnalysisUpdate = null
  } = options;
  
  // Get puzzle mode from context to handle transitions gracefully
  const { isPuzzleMode } = useContext(ChessSocialContext);
  
  const [state, dispatch] = useReducer(analysisReducer, initialState);
  const analysisRequestRef = useRef(null);
  const pollingTimeoutRef = useRef(null); // Track polling timeout for cancellation
  const isMountedRef = useRef(true);
  const internalCacheRef = useRef(new Map()); // FEN -> analysis internal cache
  const lastFenRef = useRef(null);
  const lastSentAnalysisRef = useRef(null); // Track last analysis sent to callback
  const lastSentAnalysisMapRef = useRef(new Map()); // Track last analysis sent by FEN+depth
  const lastProcessedAnalysisRef = useRef(null); // Track last processed analysis to prevent duplicates
  const analysisThrottleRef = useRef(null); // Add throttling to prevent rapid-fire analysis
  const recentResultsRef = useRef(new Map()); // Track recent results to prevent rapid duplicates
  const callbackThrottleRef = useRef(new Map()); // Throttle callback processing
  const puzzleModeTransitionRef = useRef(null); // Track puzzle mode transitions
  
  // Track puzzle mode transitions to allow more lenient deduplication during transitions
  useEffect(() => {
    if (puzzleModeTransitionRef.current !== isPuzzleMode) {
      // console.log(`ABC123 Puzzle mode transition detected: ${puzzleModeTransitionRef.current} -> ${isPuzzleMode}`);
      puzzleModeTransitionRef.current = isPuzzleMode;
      
      // Clear throttling maps during puzzle mode transitions to allow immediate updates
      callbackThrottleRef.current.clear();
      recentResultsRef.current.clear();
      lastSentAnalysisMapRef.current.clear();
      
      // console.log('ABC123 Cleared callback throttling for smooth puzzle mode transition');
    }
  }, [isPuzzleMode]);
  
  // Update FEN in state with debug logging
  useEffect(() => {
    // console.log(`ABC123 FEN state update:`, {
    //   newFen: fen?.substring(0, 50),
    //   fenExists: !!fen,
    //   fenLength: fen?.length
    // });
    dispatch({ type: 'SET_FEN', payload: fen });
  }, [fen]);
  
  // Initialize engine ready state and listen for changes
  useEffect(() => {
    // Initialize engine ready state from global window property
    const initialEngineReady = window.stockfishEngineReady || false;
    dispatch({ type: 'SET_ENGINE_READY', payload: initialEngineReady });
    
    const handleEngineReady = (event) => {
      // console.log('UnifiedHook: Engine ready event received', event.detail);
      dispatch({ type: 'SET_ENGINE_READY', payload: event.detail.ready });
    };
    
    window.addEventListener('stockfish-engine-ready', handleEngineReady);
    
    return () => {
      window.removeEventListener('stockfish-engine-ready', handleEngineReady);
    };
  }, []);
  
  // Comprehensive analysis checking function
  const checkAllAnalysisSources = useCallback(async (fenToCheck) => {
    if (!fenToCheck) return null;
    
    const cacheKey = getCacheKey(fenToCheck);
    // console.log(`ABC123 Checking all sources for FEN: ${fenToCheck.substring(0, 20)}...`);
    
    // PHASE 1: Check internal cache first (fastest)
    if (internalCacheRef.current.has(cacheKey)) {
      const cached = internalCacheRef.current.get(cacheKey);
      if (cached.depth >= targetDepth) {
        // console.log(`ABC123 Found in internal cache at depth ${cached.depth}`);
        return { analysis: cached, source: 'internal-cache' };
      }
    }
    
    // PHASE 2: Check external data provided as prop
    if (externalData && externalData.lines && externalData.lines.length > 0) {
      if (analysisMatchesCurrentFEN(externalData, fenToCheck)) {
        // console.log(`ABC123 Found external analysis data`);
        internalCacheRef.current.set(cacheKey, externalData);
        return { analysis: externalData, source: 'external' };
      }
    }
    
    // PHASE 3: Check window.currentAnalysisCache (browser cache)
    if (checkWindowCache && window.currentAnalysisCache) {
      if (analysisMatchesCurrentFEN(window.currentAnalysisCache, fenToCheck)) {
        // console.log(`ABC123 Found in window.currentAnalysisCache`);
        internalCacheRef.current.set(cacheKey, window.currentAnalysisCache);
        return { analysis: window.currentAnalysisCache, source: 'window-cache' };
      }
    }
    
    // PHASE 4: Check database/moves array cache
    if (checkDatabaseCache && window.getCachedAnalysisForFEN && typeof window.getCachedAnalysisForFEN === 'function') {
      const cachedAnalysisByFEN = window.getCachedAnalysisForFEN(fenToCheck);
      if (cachedAnalysisByFEN) {
        // console.log(`ABC123 Found in database cache`);
        internalCacheRef.current.set(cacheKey, cachedAnalysisByFEN);
        // Update global cache for consistency
        if (checkWindowCache) {
          window.currentAnalysisCache = cachedAnalysisByFEN;
        }
        return { analysis: cachedAnalysisByFEN, source: 'database-cache' };
      }
    }
    
    // PHASE 5: Check by current move index (enhanced database cache)
    if (checkDatabaseCache && 
        window.checkForCachedAnalysis && typeof window.checkForCachedAnalysis === 'function' && 
        window.getCurrentMoveIndex && typeof window.getCurrentMoveIndex === 'function') {
      
      const currentMoveIndex = window.getCurrentMoveIndex();
      const hasCachedAnalysis = window.checkForCachedAnalysis(currentMoveIndex, fenToCheck);
      
      if (hasCachedAnalysis && window.currentAnalysisCache && 
          analysisMatchesCurrentFEN(window.currentAnalysisCache, fenToCheck)) {
        // console.log(`ABC123 Found cached analysis for move index ${currentMoveIndex}`);
        internalCacheRef.current.set(cacheKey, window.currentAnalysisCache);
        return { analysis: window.currentAnalysisCache, source: 'move-index-cache' };
      }
    }
    
    // PHASE 6: Skip mock data - we want live analysis
    // Removed hardcoded FEN check per user request
    
    // PHASE 7: No cached analysis found
    // console.log(`ABC123 No cached analysis found, need to request new analysis`);
    return null;
  }, [externalData, checkWindowCache, checkDatabaseCache, forceLive, targetDepth]);
  
  // Cancel any active polling
  const cancelPolling = useCallback(() => {
    if (pollingTimeoutRef.current) {
      clearTimeout(pollingTimeoutRef.current);
      pollingTimeoutRef.current = null;
      // console.log('ABC123 Polling cancelled');
    }
  }, []);
  
  // Unified polling function to avoid multiple polling loops
  const startPolling = useCallback((fenToAnalyze, requestId) => {
    cancelPolling(); // Cancel any existing polling
    
    const startTime = Date.now();
    const pollForResults = () => {
      const elapsed = Date.now() - startTime;
      
      // Check if this request was cancelled
      if (analysisRequestRef.current !== requestId) {
        // console.log(`ABC123 Polling cancelled - newer request started`);
        return;
      }
      
      if (elapsed > 10000) { // Stop polling after 10 seconds
        // console.log(`ABC123 Polling timeout after ${elapsed}ms`);
        dispatch({ type: 'SET_ANALYZING', payload: false });
        return;
      }
      
      // Check for both pvs (Stockfish3 format) and lines (legacy format)
      if (window.currentAnalysisCache && 
          (window.currentAnalysisCache.pvs?.length > 0 || window.currentAnalysisCache.lines?.length > 0)) {
        // console.log(`ABC123 Polling found results after ${elapsed}ms:`, {
        //   hasLines: window.currentAnalysisCache.lines?.length > 0,
        //   hasPvs: window.currentAnalysisCache.pvs?.length > 0,
        //   positionFEN: window.currentAnalysisCache.positionFEN?.substring(0, 20) + '...'
        // });
        
        // Trigger the analysis result processing directly
        if (fenToAnalyze && analysisMatchesCurrentFEN(window.currentAnalysisCache, fenToAnalyze)) {
          // console.log(`ABC123 Polling analysis matches current FEN, updating state`);
          const cacheKey = getCacheKey(fenToAnalyze);
          internalCacheRef.current.set(cacheKey, window.currentAnalysisCache);
          
          dispatch({ 
            type: 'SET_ANALYSIS', 
            payload: { 
              analysis: window.currentAnalysisCache, 
              source: 'engine' 
            } 
          });
          
          // Stop polling - we got our result
          return;
        } else {
          // console.log(`ABC123 Polling analysis for different position, ignoring. Expected: ${fenToAnalyze?.substring(0, 20)}, Got: ${window.currentAnalysisCache.positionFEN?.substring(0, 20)}`);
        }
      } else {
        // console.log(`ABC123 Polling check ${elapsed}ms - no results yet`);
        if (analysisRequestRef.current === requestId) {
          pollingTimeoutRef.current = setTimeout(pollForResults, 500); // Poll every 500ms
        }
      }
    };
    
    // Start polling after a short delay
    pollingTimeoutRef.current = setTimeout(pollForResults, 500);
  }, [cancelPolling, dispatch]);
  
  // Request new analysis from engine
  const requestNewAnalysis = useCallback(async (fenToAnalyze) => {
    // console.log(`ABC123 Requesting new analysis for: ${fenToAnalyze.substring(0, 20)}...`);
    // console.log(`FORCEANALYZE: requestNewAnalysis called with FEN: ${fenToAnalyze}`);
    
    // Check if we're already analyzing this position
    if (state.isAnalyzing && state.fen === fenToAnalyze) {
      // console.log(`ABC123 Already analyzing this FEN, skipping duplicate request`);
      return;
    }
    
    // Cancel previous request and any active polling
    if (analysisRequestRef.current) {
      analysisRequestRef.current = 'cancelled';
    }
    cancelPolling();
    
    // Mark this request
    const requestId = Date.now().toString();
    analysisRequestRef.current = requestId;
    
    // console.log(`FORCEANALYZE: Setting isAnalyzing to true`);
    dispatch({ type: 'SET_ANALYZING', payload: true });
    
    try {
      // Debug what's available in window
      // console.log(`ABC123 Debug window methods:`, {
      //   hasStockfish3Instance: false, // stockfish3Instance is not exposed on window
      //   stockfish3Methods: null,
      //   hasUpdatePositionForAnalysis: typeof window.updatePositionForAnalysis === 'function',
      //   hasReceiveStockfishAnalysis: typeof window.receiveStockfishAnalysis === 'function',
      //   hasHandleStockfishAnalysisRequest: typeof window.handleStockfishAnalysisRequest === 'function'
      // });
      // console.log(`FORCEANALYZE: About to choose Stockfish integration method`);
      
      // Try different Stockfish integration methods in order of preference
      // Use the imported stockfish3Instance directly
      if (stockfish3Instance && stockfish3Instance.handleAnalysisRequest) {
        // console.log(`ABC123 Using imported stockfish3Instance.handleAnalysisRequest for FEN: ${fenToAnalyze}`);
        // console.log(`FORCEANALYZE: Taking stockfish3Instance path`);
        // console.log(`ABC123 Current window.receiveStockfishAnalysis exists: ${typeof window.receiveStockfishAnalysis === 'function'}`);
        // console.log(`ABC123 Current window.currentAnalysisCache:`, window.currentAnalysisCache ? 'exists' : 'null');
        stockfish3Instance.handleAnalysisRequest(fenToAnalyze, true);
        // console.log(`ABC123 stockfish3Instance.handleAnalysisRequest call completed`);
        
        // Use unified polling system
        startPolling(fenToAnalyze, requestId);
        
      } else if (window.handleStockfishAnalysisRequest && typeof window.handleStockfishAnalysisRequest === 'function') {
        // Use handleStockfishAnalysisRequest directly to bypass guards
        // console.log(`ABC123 Using window.handleStockfishAnalysisRequest for FEN: ${fenToAnalyze}`);
        // console.log(`FORCEANALYZE: Taking handleStockfishAnalysisRequest path`);
        window.handleStockfishAnalysisRequest(fenToAnalyze);
        // console.log(`ABC123 window.handleStockfishAnalysisRequest call completed`);
        
        // Use unified polling system
        startPolling(fenToAnalyze, requestId);
      } else if (window.updatePositionForAnalysis && typeof window.updatePositionForAnalysis === 'function') {
        // Use updatePositionForAnalysis directly if available
        // console.log(`ABC123 Using window.updatePositionForAnalysis for FEN: ${fenToAnalyze}`);
        // console.log(`FORCEANALYZE: Taking updatePositionForAnalysis path`);
        // console.log(`ABC123 Debug: About to call updatePositionForAnalysis. Current state:`, {
        //   hasFunction: typeof window.updatePositionForAnalysis === 'function',
        //   currentMove: window.getCurrentMoveIndex ? window.getCurrentMoveIndex() : 'unknown'
        // });
        window.updatePositionForAnalysis(fenToAnalyze);
        // console.log(`ABC123 window.updatePositionForAnalysis call completed`);
        
        // Use unified polling system
        startPolling(fenToAnalyze, requestId);
        
      } else {
        // Fallback to custom event
        // console.log(`ABC123 Dispatching request-stockfish-analysis event for FEN: ${fenToAnalyze}`);
        // console.log(`FORCEANALYZE: Taking custom event path`);
        // console.log(`FORCEANALYZE: About to dispatch custom event`);
        window.dispatchEvent(new CustomEvent('request-stockfish-analysis', {
          detail: {
            fen: fenToAnalyze,
            isAnalysisOnly: true,
            timestamp: Date.now()
          }
        }));
        // console.log(`ABC123 Event dispatch completed`);
        
        // Use unified polling system
        startPolling(fenToAnalyze, requestId);
      }
      
      // The actual result will come through the global callback or event
      // We set up listeners in the effect below
      
    } catch (error) {
      console.error('ABC123 Analysis request failed:', error);
      if (isMountedRef.current && analysisRequestRef.current === requestId) {
        dispatch({ type: 'SET_ERROR', payload: error.message });
      }
    }
  }, [cancelPolling, startPolling, dispatch]);
  
  // Main effect to handle FEN changes
  useEffect(() => {
    if (!fen || fen === lastFenRef.current) return;
    
    lastFenRef.current = fen;
    
    // Clear any existing throttle timeout
    if (analysisThrottleRef.current) {
      clearTimeout(analysisThrottleRef.current);
    }
    
    // Add throttling to prevent rapid-fire analysis requests that cause feedback loops
    analysisThrottleRef.current = setTimeout(async () => {
      if (!isMountedRef.current) return;
      
      // console.log(`ABC123 FEN changed, checking sources for: ${fen.substring(0, 20)}...`);
      
      const result = await checkAllAnalysisSources(fen);
      
      if (!isMountedRef.current) return;
      
      if (result) {
        // Found cached analysis
        dispatch({ 
          type: 'SET_ANALYSIS', 
          payload: { 
            analysis: result.analysis, 
            source: result.source 
          } 
        });
      } else {
        // Need to request new analysis
        await requestNewAnalysis(fen);
      }
    }, 500); // Increased to 500ms to prevent feedback loops
    
    return () => {
      if (analysisThrottleRef.current) {
        clearTimeout(analysisThrottleRef.current);
      }
    };
  }, [fen, checkAllAnalysisSources, requestNewAnalysis]);
  
  // Send analysis updates to callback when available
  useEffect(() => {
    if (!onAnalysisUpdate) return;
    
    // Send engine initializing state when engine is not ready
    if (!state.isEngineReady && !state.isAnalyzing) {
      // console.log('UnifiedHook456 sending engine initializing state to callback');
      onAnalysisUpdate({
        isCalculating: false,
        isEngineReady: false,
        fen: fen,
        positionFEN: fen,
        timestamp: Date.now()
      });
      return;
    }
    
    // Send calculating state when analysis is starting/in progress
    if (state.isAnalyzing) {
      // console.log('UnifiedHook456 sending calculating state to callback for FEN:', fen?.substring(0, 20) + '...');
      onAnalysisUpdate({
        isCalculating: true,
        isEngineReady: state.isEngineReady,
        fen: fen,
        positionFEN: fen,
        timestamp: Date.now()
      });
      return;
    }
    
    // Send analysis results when available and not analyzing
    if (state.analysis && state.analysis.pvs && state.analysis.pvs.length > 0) {
      // Only send update if analysis matches current FEN to prevent stale data
      if (analysisMatchesCurrentFEN(state.analysis, fen)) {
        // Enhanced deduplication: Track analysis by FEN+depth to prevent sending same depth multiple times
        const analysisKey = `${state.analysis.positionFEN || state.analysis.fen}_${state.analysis.depth}_${state.source}`;
        const fenDepthKey = `${state.analysis.positionFEN || state.analysis.fen}_${state.analysis.depth}`;
        
        // Check if we've already sent analysis for this FEN at this depth
        const lastSentForFenDepth = lastSentAnalysisMapRef.current.get(fenDepthKey);
        if (lastSentForFenDepth && (Date.now() - lastSentForFenDepth) < 1000) { // 1 second cooldown
          // console.log('UnifiedHook456 skipping duplicate analysis callback - same FEN+depth within 1s:', fenDepthKey);
          return;
        }
        
        if (lastSentAnalysisRef.current === analysisKey) {
          // console.log('UnifiedHook456 skipping exact duplicate analysis callback for:', analysisKey);
          return;
        }
        
        // console.log('UnifiedHook456 sending analysis results to callback:', {
        //   source: state.source,
        //   hasPvs: true,
        //   pvsLength: state.analysis.pvs.length,
        //   currentFEN: fen?.substring(0, 20) + '...',
        //   analysisFEN: (state.analysis.positionFEN || state.analysis.fen)?.substring(0, 20) + '...',
        //   depth: state.analysis.depth
        // });
        
        lastSentAnalysisRef.current = analysisKey;
        lastSentAnalysisMapRef.current.set(fenDepthKey, Date.now());
        
        // Clean up old entries from the map (keep only last 10 seconds)
        const now = Date.now();
        for (const [key, timestamp] of lastSentAnalysisMapRef.current.entries()) {
          if (now - timestamp > 10000) {
            lastSentAnalysisMapRef.current.delete(key);
          }
        }
        
        onAnalysisUpdate({
          ...state.analysis,
          isCalculating: false,
          isEngineReady: state.isEngineReady,
          source: state.source || 'unified-hook',
          timestamp: Date.now()
        });
      } else {
        // console.log('UnifiedHook456 skipping analysis update - FEN mismatch:', {
        //   currentFEN: fen?.substring(0, 20) + '...',
        //   analysisFEN: (state.analysis.positionFEN || state.analysis.fen)?.substring(0, 20) + '...'
        // });
      }
    }
  }, [state.analysis, onAnalysisUpdate, fen, state.source, state.isAnalyzing, state.isEngineReady]);
  
  // Create stable callback for handling analysis results
  const handleAnalysisResult = useCallback((result) => {
      // Reduced logging for handleAnalysisResult
      
      if (!isMountedRef.current || !result) {
        // console.log(`ABC123 handleAnalysisResult: Skipping - mounted: ${isMountedRef.current}, hasResult: ${!!result}`);
        return;
      }
      
      // ENHANCED: Add multiple levels of deduplication to prevent feedback loop
      const basicKey = `${result.fen || result.positionFEN}_${result.depth}`;
      const timestampKey = `${basicKey}_${result.timestamp || Date.now()}`;
      
      // Check for exact duplicate (with timestamp)
      if (lastProcessedAnalysisRef.current === timestampKey) {
        // console.log(`ABC123 handleAnalysisResult: PREVENTING DUPLICATE PROCESSING (exact):`, timestampKey);
        return;
      }
      
      // Check for rapid successive duplicates (same position/depth within 100ms)
      const now = Date.now();
      const recentResult = recentResultsRef.current.get(basicKey);
      if (recentResult && (now - recentResult.timestamp) < 100) {
        // console.log(`ABC123 handleAnalysisResult: PREVENTING RAPID DUPLICATE (within 100ms):`, basicKey);
        return;
      }
      
      // Record this result
      lastProcessedAnalysisRef.current = timestampKey;
      recentResultsRef.current.set(basicKey, { timestamp: now, depth: result.depth });
      
      // Clean up old entries (keep only last 10 seconds)
      for (const [key, value] of recentResultsRef.current.entries()) {
        if (now - value.timestamp > 10000) {
          recentResultsRef.current.delete(key);
        }
      }
      
      // console.log(`ABC123 Received analysis result:`, {
      //   hasResult: !!result,
      //   depth: result.depth,
      //   pvCount: result.pvs?.length || 0,
      //   lineCount: result.lines?.length || 0,
      //   positionFEN: result.positionFEN?.substring(0, 20) + '...',
      //   fen: result.fen?.substring(0, 20) + '...',
      //   currentFEN: fen?.substring(0, 20) + '...',
      //   matches: analysisMatchesCurrentFEN(result, fen),
      //   allKeys: Object.keys(result)
      // });
      
      // Validate that this result matches current FEN
      if (fen && analysisMatchesCurrentFEN(result, fen)) {
        // console.log(`ABC123 Analysis matches current FEN, updating state`);
        const cacheKey = getCacheKey(fen);
        internalCacheRef.current.set(cacheKey, result);
        
        dispatch({ 
          type: 'SET_ANALYSIS', 
          payload: { 
            analysis: result, 
            source: 'engine' 
          } 
        });
      } else {
        // console.log(`🔥 ABC123 RECEIVED ANALYSIS FOR DIFFERENT POSITION:`, {
        //   expectedFen: fen?.substring(0, 50),
        //   receivedFen: (result.positionFEN || result.fen)?.substring(0, 50),
        //   fenExists: !!fen,
        //   resultHasFen: !!(result.positionFEN || result.fen),
        //   analysisMatchResult: fen ? analysisMatchesCurrentFEN(result, fen) : 'no current fen'
        // });
        
        // ENHANCED: Allow analysis during early loading or when FEN is being initialized
        const resultFen = result.positionFEN || result.fen;
        const shouldAcceptResult = !fen || fen.length < 10 || fen === 'undefined' || !fen.includes('w') && !fen.includes('b');
        
        if (shouldAcceptResult && resultFen && resultFen.length > 10) {
          // console.log(`🔥 ABC123 ACCEPTING ANALYSIS DESPITE FEN MISMATCH (FEN state initializing):`, {
          //   currentFenState: fen,
          //   resultFen: resultFen?.substring(0, 50),
          //   reason: !fen ? 'no current FEN' : fen.length < 10 ? 'FEN too short' : 'FEN invalid format'
          // });
          
          const cacheKey = getCacheKey(resultFen);
          internalCacheRef.current.set(cacheKey, result);
          
          dispatch({ 
            type: 'SET_ANALYSIS', 
            payload: { 
              analysis: result, 
              source: 'engine' 
            } 
          });
          
          // Update FEN state to match the analysis result if current FEN is invalid
          if (!fen || fen.length < 10) {
            // console.log(`🔥 ABC123 UPDATING FEN STATE to match analysis result:`, resultFen?.substring(0, 50));
            dispatch({ type: 'SET_FEN', payload: resultFen });
          }
        }
      }
  }, [fen, dispatch]);
  
  // Set up global listeners for analysis results
  useEffect(() => {
    
    // Set up global callback hijacking (preserves existing integration)
    const originalCallback = window.receiveStockfishAnalysis;
    // console.log(`ABC123 Setting up callback hijacking. Original callback exists: ${typeof originalCallback === 'function'}`);
    
    window.receiveStockfishAnalysis = (result) => {
      try {
        // Add throttling to prevent rapid successive callback processing
        // More lenient throttling during puzzle mode transitions
        const resultKey = `${result?.positionFEN || result?.fen}_${result?.depth}`;
        const now = Date.now();
        const lastCall = callbackThrottleRef.current.get(resultKey);
        
        // Use shorter throttle period during puzzle mode transitions to prevent blinking
        const throttlePeriod = isPuzzleMode ? 25 : 50; // Reduced from 50ms to 25ms in puzzle mode
        
        if (lastCall && (now - lastCall) < throttlePeriod) {
          // console.log(`ABC123 THROTTLING callback - too rapid (${throttlePeriod}ms):`, resultKey);
          return;
        }
        callbackThrottleRef.current.set(resultKey, now);
        
        // console.log(`🔥 ABC123 UNIFIED HOOK receiveStockfishAnalysis:`, {
        //   hasPvs: !!(result?.pvs && result.pvs.length > 0),
        //   depth: result?.depth,
        //   fen: result?.positionFEN?.substring(0, 30) + '...'
        // });
        
        // Cancel any active polling since we got a result
        cancelPolling();
        
        // IMPORTANT: Call the original callback FIRST to ensure window.currentAnalysisCache gets updated
        if (typeof originalCallback === 'function') {
          originalCallback(result);
        }
        
        // Use the result that has more data - prefer window.currentAnalysisCache if it has data
        let resultToUse = result;
        if (window.currentAnalysisCache && (window.currentAnalysisCache.lines?.length > 0 || window.currentAnalysisCache.pvs?.length > 0)) {
          resultToUse = window.currentAnalysisCache;
        }
        
        // Handle the result only once
        handleAnalysisResult(resultToUse);
        
      } catch (error) {
        console.error('ABC123 Error in Stockfish callback:', error);
      }
    };
    
    // NOTE: Removed event listeners since callback hijacking handles all analysis results
    // This prevents duplicate processing that was causing d=10,d=15,d=10,d=15 pattern
    
    return () => {
      // Restore original callback
      if (originalCallback) {
        window.receiveStockfishAnalysis = originalCallback;
      }
    };
  }, [handleAnalysisResult, cancelPolling]); // Include cancelPolling dependency
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      if (analysisRequestRef.current) {
        analysisRequestRef.current = 'cancelled';
      }
      if (analysisThrottleRef.current) {
        clearTimeout(analysisThrottleRef.current);
        analysisThrottleRef.current = null;
      }
      cancelPolling();
    };
  }, [cancelPolling]);
  
  // Listen for stockfish-analyze-position events from 2AddGame.js
  useEffect(() => {
    const handleStockfishAnalyzeEvent = (event) => {
      if (!event.detail || !event.detail.fen) return;
      
      const { fen: eventFen, forceCalculation } = event.detail;
      // console.log(`ABC123 Received stockfish-analyze-position event for FEN: ${eventFen}`);
      
      // Only process if it's for our current FEN or if force is true
      if (forceCalculation || eventFen === fen) {
        // console.log(`ABC123 Processing stockfish-analyze-position event`);
        // Clear cache and request new analysis
        const cacheKey = getCacheKey(eventFen);
        internalCacheRef.current.delete(cacheKey);
        requestNewAnalysis(eventFen);
      }
    };
    
    window.addEventListener('stockfish-analyze-position', handleStockfishAnalyzeEvent);
    
    return () => {
      window.removeEventListener('stockfish-analyze-position', handleStockfishAnalyzeEvent);
    };
  }, [fen, requestNewAnalysis]);
  
  // Expose forceReanalyze globally for external triggers
  useEffect(() => {
    const globalForceReanalyze = (targetFen = null) => {
      const fenToUse = targetFen || fen;
      // console.log(`FORCEANALYZE: globalForceReanalyze called with targetFen: ${targetFen}, fen: ${fen}, fenToUse: ${fenToUse}`);
      
      if (fenToUse) {
        // console.log(`ABC123 Global forceReanalyze called for FEN: ${fenToUse}`);
        // console.log(`FORCEANALYZE: Clearing internal cache for key: ${getCacheKey(fenToUse)}`);
        internalCacheRef.current.delete(getCacheKey(fenToUse));
        
        // Clear window cache as well
        if (window.currentAnalysisCache) {
          // console.log(`FORCEANALYZE: Clearing window.currentAnalysisCache`);
          window.currentAnalysisCache = null;
        }
        
        // console.log(`FORCEANALYZE: Calling requestNewAnalysis for: ${fenToUse}`);
        requestNewAnalysis(fenToUse);
      } else {
        // console.log(`FORCEANALYZE: No FEN to analyze - targetFen: ${targetFen}, fen: ${fen}`);
      }
    };
    
    // Debug function to check cache state
    const debugCacheState = () => {
      // console.log(`DEBUG_CACHE: Current state:`, {
      //   fen: fen?.substring(0, 20),
      //   hasWindowCache: !!window.currentAnalysisCache,
      //   windowCacheKeys: window.currentAnalysisCache ? Object.keys(window.currentAnalysisCache) : null,
      //   hasLines: window.currentAnalysisCache?.lines?.length > 0,
      //   hasPvs: window.currentAnalysisCache?.pvs?.length > 0,
      //   cachePositionFEN: window.currentAnalysisCache?.positionFEN?.substring(0, 20),
      //   cacheFen: window.currentAnalysisCache?.fen?.substring(0, 20),
      //   isAnalyzing: state.isAnalyzing,
      //   hasAnalysis: !!state.analysis,
      //   analysisSource: state.source
      // });
    };
    
    // console.log(`FORCEANALYZE: Setting up window.forceStockfishReanalysis global function`);
    window.forceStockfishReanalysis = globalForceReanalyze;
    window.debugUnifiedHookCache = debugCacheState;
    
    return () => {
      delete window.forceStockfishReanalysis;
      delete window.debugUnifiedHookCache;
    };
  }, [fen, requestNewAnalysis, state]);

  // Public API
  return {
    analysis: state.analysis,
    isAnalyzing: state.isAnalyzing,
    isEngineReady: state.isEngineReady,
    depth: state.depth,
    source: state.source,
    error: state.error,
    lastUpdated: state.lastUpdated,
    
    // Utility functions
    clearCache: () => {
      internalCacheRef.current.clear();
      // console.log('ABC123 Internal cache cleared');
    },
    
    forceReanalyze: () => {
      if (fen) {
        internalCacheRef.current.delete(getCacheKey(fen));
        requestNewAnalysis(fen);
      }
    },
    
    // Debug info
    getCacheSize: () => internalCacheRef.current.size,
    getCacheKeys: () => Array.from(internalCacheRef.current.keys())
  };
};

export default useUnifiedStockfishAnalysis;